import { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, ShieldCheck } from 'lucide-react';

// Member self-check (2026-10-02), public page /check — no login needed so it can be
// linked from the setup manual. In July the most common reason a member's ads were not
// captured was an untrusted certificate (18 of 24 cases).
//   1. VPN on?  GET /api/vpncheck — the backend sees the member's VPN address (traffic to
//      this portal bypasses the proxy by design).
//   2. Certificate trusted?  Only when the VPN is on: load a page that DOES go through the
//      proxy (QUIC is blocked on the VPN, so it cannot slip past). The proxy presents our
//      certificate; the load succeeds only if the phone trusts it. no-cors: any HTTP answer
//      counts as success, only a TLS/network failure counts as failure.

type Result = 'idle' | 'checking' | 'ok' | 'off' | 'fail' | 'neterr';

const CERT_PROBE = 'https://www.youtube.com/generate_204';

const TEXT = {
  ja: {
    title: '接続チェック',
    lead: 'VPNと証明書が正しく設定され、広告が記録される状態になっているかを確認します。',
    steps: ['このページを開いたまま、WireGuard アプリで VPN を「オン」にします。', '下の「チェックする」を押します。'],
    note: '赤い表示が出た場合は、証明書の設定手順に沿って設定し直してから、もう一度チェックしてください（VPNはオンのままで大丈夫です）。',
    button: 'チェックする', checking: '確認中…',
    ok: 'VPN接続中・証明書も正しく設定されています。広告の記録が行われます。',
    okSub: '接続番号',
    off: 'VPNがオフになっています。WireGuard アプリで VPN をオンにしてから、もう一度チェックしてください。',
    fail: 'VPNはオンですが、証明書が使えていません。証明書がインストールされていないか、「信頼」の設定がされていない可能性が高いです。',
    failSub: 'この状態では広告が記録されません。',
    neterr: 'サーバーに接続できませんでした。インターネットにつながっているか確認して、もう一度チェックしてください。',
    guide: '証明書の設定手順を見る', lang: 'English',
  },
  en: {
    title: 'Connection check',
    lead: 'Checks that the VPN and the certificate are set up so that ads can be recorded.',
    steps: ['Keep this page open and turn the VPN ON in the WireGuard app.', 'Press "Check" below.'],
    note: 'If you see a red result, set up the certificate again following the guide, then check again (the VPN can stay on).',
    button: 'Check', checking: 'Checking…',
    ok: 'VPN is on and the certificate is set up correctly. Ads will be recorded.',
    okSub: 'Connection',
    off: 'The VPN is off. Turn the VPN on in the WireGuard app and check again.',
    fail: 'The VPN is on, but the certificate is not working. It is most likely not installed or not set to "trusted".',
    failSub: 'Ads cannot be recorded in this state.',
    neterr: 'Could not reach the server. Check that you are connected to the internet and try again.',
    guide: 'Open the certificate setup guide', lang: '日本語',
  },
};

export default function ConnectionCheck() {
  const [lang, setLang] = useState<'ja' | 'en'>('ja');
  const [result, setResult] = useState<Result>('idle');
  const [vpnIp, setVpnIp] = useState<string | null>(null);
  const t = TEXT[lang];

  async function check() {
    setResult('checking'); setVpnIp(null);
    const timed = (url: string, init: RequestInit) => {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 8000);
      return fetch(url, { ...init, cache: 'no-store', signal: ctl.signal }).finally(() => clearTimeout(timer));
    };
    let ip: string | null = null;
    try {
      const j = await (await timed(`/api/vpncheck?t=${Date.now()}`, {})).json();
      if (j.vpn !== true) { setResult('off'); return; }
      ip = j.vpnIp ?? null;
    } catch {
      setResult('neterr'); return;
    }
    try {
      await timed(`${CERT_PROBE}?t=${Date.now()}`, { mode: 'no-cors' });
      setVpnIp(ip); setResult('ok');
    } catch {
      setResult('fail');
    }
  }

  const box = (cls: string, icon: JSX.Element, main: string, sub?: string, link?: boolean) => (
    <div className={`mt-5 rounded-xl border-l-4 p-4 ${cls}`}>
      <div className="flex gap-3">
        <div className="shrink-0 mt-0.5">{icon}</div>
        <div>
          <p className="font-semibold">{main}</p>
          {sub && <p className="text-sm mt-1 opacity-90">{sub}</p>}
          {link && <a href="/portal/setup" className="inline-block mt-2 text-sm font-semibold underline">{t.guide} →</a>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-teal-600" />{t.title}
          </h1>
          <button onClick={() => setLang(lang === 'ja' ? 'en' : 'ja')} className="text-xs text-blue-700 underline">{t.lang}</button>
        </div>
        <p className="text-gray-700 text-sm mb-4">{t.lead}</p>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-800 mb-4">
          {t.steps.map((s) => <li key={s}>{s}</li>)}
        </ol>
        <button onClick={check} disabled={result === 'checking'}
          className="w-full rounded-xl bg-teal-600 py-3 text-white font-semibold hover:bg-teal-700 disabled:opacity-60 flex items-center justify-center gap-2">
          {result === 'checking' && <Loader2 className="w-5 h-5 animate-spin" />}
          {result === 'checking' ? t.checking : t.button}
        </button>

        {result === 'ok' && box('border-emerald-500 bg-emerald-50 text-emerald-900',
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />, t.ok, vpnIp ? `${t.okSub}: ${vpnIp}` : undefined)}
        {result === 'off' && box('border-amber-500 bg-amber-50 text-amber-900',
          <AlertTriangle className="w-6 h-6 text-amber-600" />, t.off)}
        {result === 'fail' && box('border-red-500 bg-red-50 text-red-900',
          <XCircle className="w-6 h-6 text-red-600" />, t.fail, t.failSub, true)}
        {result === 'neterr' && box('border-gray-400 bg-gray-50 text-gray-900',
          <AlertTriangle className="w-6 h-6 text-gray-600" />, t.neterr)}

        <p className="mt-6 text-xs text-gray-500 leading-relaxed">{t.note}</p>
      </div>
    </div>
  );
}
